import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import { applyBranding } from "@/lib/branding";
import { toast } from "sonner";
import { Sparkles, Eye, Save, RotateCcw, Upload } from "lucide-react";

const MODES = ["STANDARD", "CO_BRANDED", "WHITE_LABEL"];
const COLOURS = [["primary_colour", "Primary"], ["secondary_colour", "Secondary"], ["accent_colour", "Accent"], ["surface_colour", "Surface"], ["text_colour", "Text"]];

export default function Branding() {
  const [draft, setDraft] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState({ corporate_website: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/branding/admin");
    setDraft(data.draft);
    setWarnings(data.contrast_warnings || []);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const saveDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/branding/admin/draft", draft);
      setWarnings(data.contrast_warnings || []);
      toast.success("Draft saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    try { await api.post("/branding/admin/publish"); toast.success("Branding published"); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const revert = async () => {
    try { const { data } = await api.post("/branding/admin/revert"); setDraft(data.draft); applyBranding(null); toast.success("Reverted to CaterCTRL default"); }
    catch { toast.error("Revert failed"); }
  };
  const runAI = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/branding/admin/import-ai", ai);
      setDraft(data.draft); setWarnings(data.contrast_warnings || []);
      toast.success("Brand draft imported — review & publish");
    } catch (e) { toast.error(e.response?.data?.detail || "Import unavailable"); }
    finally { setBusy(false); }
  };
  const preview = () => { applyBranding(draft); toast.message("Previewing draft — publish to keep it"); };

  if (!draft) return null;

  return (
    <div data-testid="branding-page" className="max-w-2xl">
      <PageHeader title="Branding" subtitle="Your organisation's identity, inherited by every site. Ordinary staff can't change this." testid="branding-header" />

      {warnings.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800" data-testid="branding-contrast-warning">
          Low contrast: {warnings.map((w) => `${w.pair} ${w.ratio}:1 (min ${w.min})`).join(", ")}. Text/background must be readable to publish.
        </div>
      )}

      <div className="cc-card p-5 mb-4 space-y-3">
        <div><Label className="text-xs font-semibold text-slate-600">Display name</Label>
          <Input value={draft.display_name || ""} onChange={(e) => set("display_name", e.target.value)} data-testid="branding-display-name" className="mt-1.5" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Brand mode</Label>
          <select value={draft.branding_mode} onChange={(e) => set("branding_mode", e.target.value)} data-testid="branding-mode"
            className="mt-1.5 w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2">
            {MODES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
          </select></div>
      </div>

      <div className="cc-card p-5 mb-4">
        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Colours</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {COLOURS.map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs text-slate-500">{l}</Label>
              <input type="color" value={draft[k] || "#000000"} onChange={(e) => set(k, e.target.value)} data-testid={`branding-${k}`} className="mt-1 h-9 w-full rounded border border-slate-200" />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">Danger, warning and food-safety status colours are fixed and never themed.</p>
      </div>

      <div className="cc-card p-5 mb-4 space-y-3" data-testid="branding-logo-section">
        <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Upload size={14} /> Primary logo</Label>
        <PhotoUpload value={draft.primary_logo_file_id} onChange={(fid) => set("primary_logo_file_id", fid)} label="Upload logo" testid="branding-logo-upload" />
      </div>

      <div className="cc-card p-5 mb-4 space-y-2" data-testid="branding-ai-section">
        <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Sparkles size={14} className="text-brand" /> Import brand with AI</Label>
        <Input placeholder="https://yourcompany.co.uk" value={ai.corporate_website} onChange={(e) => setAi({ ...ai, corporate_website: e.target.value })} data-testid="branding-ai-website" />
        <Textarea placeholder="Anything about your brand (optional)" value={ai.notes} onChange={(e) => setAi({ ...ai, notes: e.target.value })} data-testid="branding-ai-notes" />
        <Button variant="outline" onClick={runAI} disabled={busy} data-testid="branding-ai-run"><Sparkles size={15} className="mr-1" /> Propose branding (draft only)</Button>
      </div>

      <div className="flex flex-wrap gap-2 sticky bottom-0 bg-slate-50 py-3">
        <Button variant="outline" onClick={preview} data-testid="branding-preview"><Eye size={15} className="mr-1" /> Preview</Button>
        <Button onClick={saveDraft} disabled={busy} data-testid="branding-save-draft" className="bg-slate-700 hover:bg-slate-800"><Save size={15} className="mr-1" /> Save draft</Button>
        <Button onClick={publish} disabled={busy} data-testid="branding-publish" className="bg-brand hover:bg-brand-hover"><Sparkles size={15} className="mr-1" /> Publish</Button>
        <Button variant="ghost" onClick={revert} data-testid="branding-revert" className="text-slate-500 ml-auto"><RotateCcw size={15} className="mr-1" /> Revert to CaterCTRL</Button>
      </div>
    </div>
  );
}
